import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  CartAddEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles the line item removal.
   *
   * Confirm-then-update (no optimistic update): the row shows a spinner while the
   * server processes the removal, and the slide-out animation plays only after the
   * server confirms. On unreliable connections this keeps the cart consistent —
   * nothing changes until the server responds, and a failure leaves the cart intact.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows[line - 1];
    const rows = row
      ? [row, ...this.refs.cartItemRows.filter((nested) => nested.dataset.parentKey === row.dataset.key)]
      : [];

    // Spinner on the affected row(s) while the request is in flight.
    rows.forEach((nested) => nested.classList.add('cart-items__table-row--deleting'));

    this.updateQuantity({ line, quantity: 0, action: 'clear', removeRows: rows });
  }

  /**
   * Plays the row slide-out on already-confirmed rows, resolving once it finishes
   * so the section can be re-rendered. Swaps the in-flight spinner for the
   * trash/collapse animation.
   * @param {HTMLElement[]} rows
   * @returns {Promise<unknown>}
   */
  #animateRowsRemoval(rows) {
    rows.forEach((row) => {
      row.classList.remove('cart-items__table-row--deleting');
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
    });

    return onAnimationEnd(rows, () => {});
  }

  /**
   * Increases a cart line's quantity by one (bound from the line's "+" button).
   * @param {number} line - The 1-based cart line index.
   */
  increaseLineQuantity(line) {
    this.#stepLineQuantity(line, 1);
  }

  /**
   * Decreases a cart line's quantity by one (bound from the line's "-" button).
   * @param {number} line - The 1-based cart line index.
   */
  decreaseLineQuantity(line) {
    this.#stepLineQuantity(line, -1);
  }

  /**
   * Routes the "Have a code?" prompt to checkout by activating the primary Checkout
   * submit, so it goes through whatever Razorpay Magic Checkout bound to that button.
   * A second name="checkout" submit could fall through to native Shopify checkout if
   * Magic only enhanced the primary button, so we click the primary button instead.
   * No-op when the cart is empty (the button is disabled).
   */
  proceedToCheckout() {
    const checkoutButton = this.querySelector('.cart-drawer-summary__checkout-button');
    if (checkoutButton instanceof HTMLButtonElement && !checkoutButton.disabled) {
      checkoutButton.click();
    }
  }

  /**
   * Steps a cart line's quantity by delta within [min, max] and updates the cart.
   * Reads the current value and bounds from the line's quantity output; the shown
   * value refreshes when the section morphs after the update completes.
   * @param {number} line - The 1-based cart line index.
   * @param {number} delta - +1 or -1.
   */
  #stepLineQuantity(line, delta) {
    const lineNumber = Number(line);
    const row = this.refs.cartItemRows[lineNumber - 1];
    const valueElement = row?.querySelector('.cart-quantity__value');
    if (!(valueElement instanceof HTMLElement)) return;

    const current = parseInt(valueElement.dataset.quantity ?? '', 10);
    if (Number.isNaN(current)) return;

    const min = parseInt(valueElement.dataset.min ?? '', 10) || 1;
    const max = valueElement.dataset.max ? parseInt(valueElement.dataset.max, 10) : Infinity;
    const next = Math.min(max, Math.max(min, current + delta));
    if (next === current) return;

    const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
    textComponent?.shimmer();

    this.updateQuantity({ line: lineNumber, quantity: next, action: 'change' });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {HTMLElement[]} [config.removeRows] - Rows being removed; when present, the
   *   confirmed slide-out animation plays before the section re-renders.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        if (parsedResponseText.errors) {
          resetShimmer(this);
          this.#handleCartError(line, parsedResponseText);
          // Clear the in-flight spinner; the cart is unchanged (nothing to revert).
          config.removeRows?.forEach((row) => row.classList.remove('cart-items__table-row--deleting'));
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const morph = () => {
          morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.morphMode,
          });
          resetShimmer(this);
        };

        // For removals, play the confirmed slide-out first (the total keeps
        // shimmering), then re-render. Other updates re-render immediately.
        if (config.removeRows?.length && !prefersReducedMotion()) {
          return this.#animateRowsRemoval(config.removeRows).then(morph);
        }

        morph();
      })
      .catch((error) => {
        console.error(error);
        resetShimmer(this);
        // Clear any in-flight delete spinner so the control isn't left stuck.
        config.removeRows?.forEach((row) => row.classList.remove('cart-items__table-row--deleting'));
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // The displayed quantity isn't changed optimistically (it refreshes on the
    // post-update morph), so a rejected change needs no revert — just surface the
    // per-line error.
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    // The cart drawer stores its open/closed state as an [open] attribute on the
    // section-root <cart-drawer-component> (assets/cart-drawer.js). A full morph
    // reconciles that wrapper against the server HTML — which is always rendered
    // closed — and strips [open], snapping a just-opened drawer shut on the very
    // add that opened it. Hydration mode only morphs [data-hydration-key] targets
    // (the drawer's inner content), leaving the wrapper and its open state intact.
    const mode = this.morphMode;
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml, { mode });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false, mode });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * Morph strategy for this component's section. The drawer must hydrate rather
   * than full-morph so its section-root [open] state — and any other transient
   * UI state carried on the wrapper — survives cart re-renders; the standalone
   * cart page has no such state and full-morphs.
   * @returns {'hydration' | 'full'}
   */
  get morphMode() {
    return this.isDrawer ? 'hydration' : 'full';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
